import { z } from 'zod'

import { isAmountString } from '@/shared/lib/money'

/**
 * Wire primitives shared by every feature's schemas.
 */

/**
 * A monetary value. Always a string on the wire — validating it as such is
 * what stops a `number` from ever reaching the money code (hard rule 1).
 */
export const money = z
  .string()
  .refine(isAmountString, { message: 'expected a decimal amount string' })

/**
 * ULID — ids and cursors (backend-routes.md §1).
 *
 * Checked by shape, not by alphabet. A strict Crockford base32 test (which
 * excludes I, L, O and U) rejects **19 of the 45** sample ids in the route
 * map — `01JAV3QR1B4E7H0K3N6R9U2X5A` among them — so the documented examples
 * are hand-written placeholders rather than real ULIDs. Validating strictly
 * would fail the contract's own responses, and there is no way to tell from
 * here whether production emits real ULIDs.
 *
 * Tighten this to /^[0-9A-HJKMNP-TV-Z]{26}$/ once the backend's actual id
 * format is confirmed.
 */
export const ulid = z.string().regex(/^[0-9A-Z]{26}$/, 'expected a 26-character id')

/** ISO-8601 with offset. */
export const timestamp = z.iso.datetime({ offset: true })

/**
 * Every `status` is the persisted enum verbatim, with a display value beside
 * it as `status_label_fa` (backend-routes.md §1). Schemas pair them so no
 * feature re-derives a label from a code.
 */
export function statusWithLabel<T extends readonly [string, ...string[]]>(values: T) {
  return z.object({
    status: z.enum(values),
    status_label_fa: z.string(),
  })
}

/** Cursor pagination: `?after=<id>&limit=<n>` → `{ items, next_cursor }`. */
export function paginated<T extends z.ZodType>(item: T) {
  return z.object({
    items: z.array(item),
    next_cursor: ulid.nullable(),
  })
}

/** A human reference the user can quote to support: `SX-441208`. */
export const referenceNo = z.string()

/**
 * A field the service may send as `null` rather than omit.
 *
 * FastAPI serialises `str | None = None` as an explicit `null`, so a plain
 * `.optional()` — which only permits `undefined` — rejects the live payload.
 * Every optional field coming from the backend should use this instead; the
 * first one that did not cost a screen stuck on a retry loop.
 */
export function nullish<T extends z.ZodType>(schema: T) {
  return schema.nullable().optional()
}
